import {Component, ElementRef, HostListener, Inject, Optional, ViewChild} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  faDownLeftAndUpRightToCenter,
  faDownload,
  faUpRightAndDownLeftFromCenter
} from "@fortawesome/free-solid-svg-icons";

type Mode = 'fit' | 'full';

@Component({
    selector: 'view-full-size-image',
    templateUrl: './view-full-size-image.component.html',
    styleUrl: './view-full-size-image.component.scss',
    standalone: false
})
export class ViewFullSizeImageModalComponent {

  @ViewChild('viewer', { static: false }) viewerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('img', { static: false }) imgRef!: ElementRef<HTMLImageElement>;

  naturalWidth = 0;
  naturalHeight = 0;
  thumbnail: any = null
  mode: Mode = 'full';
  scale = 1;
  fitScale = 1;

  protected readonly faDownload = faDownload;
  protected readonly faDownLeftAndUpRightToCenter = faDownLeftAndUpRightToCenter;
  protected readonly faUpRightAndDownLeftFromCenter = faUpRightAndDownLeftFromCenter;

  constructor(
    public dialogRef: MatDialogRef<ViewFullSizeImageModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.thumbnail = data.thumbnail
    dialogRef.addPanelClass("panel")
    dialogRef.updateSize("auto", "auto")
  }

  onImageLoad(e: Event) {
    const img = e.target as HTMLImageElement;
    this.naturalHeight = img.naturalHeight;
    this.naturalWidth = img.naturalWidth;
    img.style.width = `${this.naturalWidth}px`;
    this.computeFit();
    this.applyScale();
  }

  @HostListener('window:resize') onResize() {
    this.computeFit();
    this.applyScale();
  }

  private computeFit(): void {
    const viewer = this.viewerRef?.nativeElement;
    if (!this.naturalWidth || !this.naturalHeight) return;

    const s = Math.min(viewer.clientWidth / this.naturalWidth, viewer.clientHeight / this.naturalHeight, 1);
    this.fitScale = Number.isFinite(s) && s > 0 ? s : 1;
    if (this.mode === 'fit') {
      this.scale = this.fitScale;
    }
  }

  private applyScale(): void {
    const imgEl = this.imgRef?.nativeElement;
    if (!imgEl) return;
    imgEl.style.transform = `scale(${this.scale})`;
  }

  zoomIn() {
    this.mode = 'full';
    this.scale = Math.min(this.scale * 1.25, 8);
    this.applyScale();
  }

  zoomOut() {
    this.mode = 'full';
    this.scale = Math.max(this.scale / 1.25, 0.1);
    this.applyScale();
  }

  toggleFitFull() {
    this.mode = this.mode === 'fit' ? 'full' : 'fit';
    this.scale = this.mode === 'fit' ? this.fitScale : 1;
    this.applyScale();
  }

  private mimeFromExt(ext?: string) {
    switch ((ext || '').toLowerCase()) {
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'gif': return 'image/gif';
      case 'tif':
      case 'tiff': return 'image/tiff';
      case 'bmp': return 'image/bmp';
      case 'webp': return 'image/webp';
      default: return 'application/octet-stream';
    }
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    const mime = /data:(.*?);base64/.exec(header)?.[1] || 'application/octet-stream';
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.codePointAt(i) ?? 0;
    return new Blob([bytes], { type: mime });
  }

  async downloadImage() {
    if (!this.thumbnail?.image) return;
    const ensureDataUrl = (b64: string, ext?: string) => {
      if (b64.startsWith('data:')) return b64;
      const mime = this.mimeFromExt(ext);
      return `data:${mime};base64,${b64}`;
    }
    const dataUrl = ensureDataUrl(this.thumbnail.image, this.thumbnail.extension);
    const blob = this.dataUrlToBlob(dataUrl);
    const url = URL.createObjectURL(blob);
    const fileName = (this.thumbnail.name?.trim()) ||
      `image.${(this.thumbnail.extention || 'png').toLowerCase()}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

}
